'use client'

import { useEffect, useMemo, useState } from 'react'
import { StrapiImage } from '@/components/StrapiImage'
import Link from 'next/link'
import { Heart, MapPin, X } from 'lucide-react'
import { useFavoriteServices } from '@/hooks/useFavoriteServices'
import {
  fetchMarketplaceServices,
  serviceCarouselImageUrls,
  serviceDetailPath,
  type MarketplaceService,
} from '@/lib/marketplace-services'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'

export function FavoritesSheet({
  className,
  triggerClassName,
  showLabel,
}: {
  className?: string
  /** e.g. compact icon-only on mobile header */
  triggerClassName?: string
  showLabel?: boolean
}) {
  const { favoriteIds, toggleFavorite } = useFavoriteServices()
  const [open, setOpen] = useState(false)
  const [services, setServices] = useState<MarketplaceService[]>([])
  const [loading, setLoading] = useState(false)
  /** Favorites come from localStorage — must match SSR (empty) until mount. */
  const [hasMounted, setHasMounted] = useState(false)
  useEffect(() => {
    setHasMounted(true)
  }, [])

  const idsKey = useMemo(
    () =>
      [...favoriteIds].sort((a, b) => a - b).join(','),
    [favoriteIds]
  )

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    fetchMarketplaceServices()
      .then((all) => {
        if (cancelled) return
        const set = new Set(favoriteIds)
        setServices(all.filter((s) => set.has(s.id)))
      })
      .catch(() => {
        if (!cancelled) setServices([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, idsKey, favoriteIds])

  const count = hasMounted ? favoriteIds.size : 0

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={showLabel ? 'default' : 'icon'}
          className={cn(
            'relative gap-2',
            !showLabel && 'h-10 w-10',
            triggerClassName
          )}
          aria-label={
            hasMounted && count > 0 ? `Omiljene usluge (${count})` : 'Omiljene usluge'
          }
        >
          <Heart
            className={cn(
              'h-5 w-5 min-h-[24px] min-w-[24px] transition-[fill,color]',
              hasMounted && count > 0
                ? 'fill-destructive/85 text-destructive'
                : 'text-foreground/80'
            )}
            strokeWidth={1.75}
          />
          {showLabel && (
            <span className="text-sm font-medium">Omiljene</span>
          )}
          {hasMounted && count > 0 && (
            <span className="absolute flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-background bg-primary px-1 text-[10px] font-bold leading-none text-primary-foreground shadow-sm max-lg:right-[5px] max-lg:top-[6px] lg:-right-0 lg:-top-0">
              {count > 99 ? '99+' : count}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className={cn('flex w-full flex-col sm:max-w-md', className)}>
        <SheetHeader className="border-b border-border/70 pb-4 text-left">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <Heart className="h-5 w-5 fill-destructive/85 text-destructive" />
            Sačuvane usluge
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-4 md:!px-7 pb-7 !pt-0">
          {loading ? (
            <p className="text-sm text-muted-foreground">Učitavanje…</p>
          ) : count === 0 ? (
            <p className="text-sm text-muted-foreground">
              Još nema sačuvanih usluga. Dodirnite srce na kartici usluge da je sačuvate.
            </p>
          ) : services.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nije moguće učitati usluge. Proverite konekciju ili pokušajte ponovo.
            </p>
          ) : (
            <ul className="divide-y divide-border/70">
              {services.map((s) => (
                <li key={s.documentId ?? s.id} className="py-5">
                  <Link
                    href={serviceDetailPath(s)}
                    className="group flex items-center gap-3 rounded-xl px-1 py-1 transition-colors hover:bg-muted/40"
                    onClick={() => setOpen(false)}
                  >
                    <div className="relative h-[80px] w-[80px] shrink-0 overflow-hidden rounded-md bg-muted">
                      <StrapiImage
                        src={serviceCarouselImageUrls(s)[0] ?? '/placeholder.svg'}
                        alt={s.title}
                        fill
                        sizes="80px"
                        className="object-cover"
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-2 text-base font-semibold leading-[1.15] tracking-tight text-black hover:text-primary dark:text-white dark:hover:text-white/90">
                        {s.title}
                      </p>
                      <div className="mt-2 inline-flex h-10 items-center gap-1.5 rounded-[10px] bg-[#F7F8FF] px-3 text-base font-semibold leading-none text-[#111111] dark:bg-white/10 dark:text-white">
                        <MapPin className="h-4 w-4 shrink-0 text-[#000000] dark:text-white" />
                        <span className="text-xs font-medium leading-[1.5] text-[#111111] dark:text-white">
                          {(s.locations?.[0]?.locationName ?? 'Lokacija').trim() || 'Lokacija'}
                        </span>
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      noScale
                      className="h-8 w-8 shrink-0 rounded-md bg-[#F7F8FF] text-black/90 transition-colors hover:bg-[#ECEEFA] dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
                      aria-label="Ukloni iz omiljenih"
                      onClick={async (e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        await toggleFavorite(s)
                      }}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
